import { useState, useCallback, useEffect, useRef } from 'react';
import { useUpdateBadgeDisplayMutation } from 'store/services/badge/badgeApi';
import { UserBadge } from 'types/interfaces/badge';

interface SelectedBadge {
  badgeId: number;
  order: number;
}

interface UseBadgeAutoSaveOptions {
  initialDisplayed: UserBadge[] | undefined;
  maxSlots: number;
  isOwnProfile: boolean;
}

export const useBadgeAutoSave = ({
  initialDisplayed,
  maxSlots,
  isOwnProfile,
}: UseBadgeAutoSaveOptions) => {
  const [selectedBadges, setSelectedBadges] = useState<SelectedBadge[]>([]);
  const [updateDisplay] = useUpdateBadgeDisplayMutation();
  const [isSaving, setIsSaving] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const hasPendingSave = useRef(false);

  // Sync from server initial data (only when no pending save)
  useEffect(() => {
    if (!initialDisplayed || hasPendingSave.current) return;
    setSelectedBadges(
      initialDisplayed
        .filter((ub) => ub.displayOrder !== null)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        .map((ub, i) => ({ badgeId: ub.badgeId, order: i + 1 }))
    );
  }, [initialDisplayed]);

  // Debounced save
  const save = useCallback(
    (badges: SelectedBadge[]) => {
      if (!isOwnProfile) return;
      hasPendingSave.current = true;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        setIsSaving(true);
        try {
          await updateDisplay({ badges }).unwrap();
        } catch {
          // Error handled by RTK Query
        } finally {
          setIsSaving(false);
          hasPendingSave.current = false;
        }
      }, 500);
    },
    [isOwnProfile, updateDisplay]
  );

  const toggleBadge = useCallback(
    (badgeId: number) => {
      setSelectedBadges((prev) => {
        const existing = prev.find((s) => s.badgeId === badgeId);
        let next: SelectedBadge[];

        if (existing) {
          // Remove and re-index
          next = prev.filter((s) => s.badgeId !== badgeId).map((s, i) => ({ ...s, order: i + 1 }));
        } else if (maxSlots === 1) {
          // Free user: replace
          next = [{ badgeId, order: 1 }];
        } else if (prev.length >= maxSlots) {
          // At max, replace the last badge (highest display_order)
          next = [...prev.slice(0, -1), { badgeId, order: prev.length }];
        } else {
          // Add
          next = [...prev, { badgeId, order: prev.length + 1 }];
        }

        save(next);
        return next;
      });
    },
    [maxSlots, save]
  );

  const reorderBadges = useCallback(
    (badges: SelectedBadge[]) => {
      const reindexed = badges.map((b, i) => ({ ...b, order: i + 1 }));
      setSelectedBadges(reindexed);
      save(reindexed);
    },
    [save]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    selectedBadges,
    toggleBadge,
    reorderBadges,
    isSaving,
    isSelected: (badgeId: number) => selectedBadges.some((s) => s.badgeId === badgeId),
  };
};
